/**
 * Native home-screen widget bridge (WidgetBridge Capacitor plugin).
 *
 * The native side (Swift/Java) stores a READ-only workspace API key where the
 * widget extension/provider can reach it (iOS App Group UserDefaults, Android
 * SharedPreferences) and reloads the widget timelines. The web app never
 * reads the key back — it only sets, clears, and asks "is a key stored?".
 *
 * Deep links: widgets open `com.buzzbitx.app://widget[?dest=inbox]`;
 * `initWidgetDeepLinks` routes those into the app shell. Registered as a
 * sibling of nativeAuth's listener (which only handles auth/callback URLs).
 *
 * All functions are no-ops on web (Capacitor.isNativePlatform() === false).
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { logger } from './logger';

export interface WidgetCredentials {
  apiKey: string;
  keyId: string;
  workspaceId: string;
  apiUrl: string;
}

export interface WidgetBridgeStatus {
  configured: boolean;
  keyId: string | null;
}

interface WidgetBridgePlugin {
  setCredentials(creds: WidgetCredentials): Promise<void>;
  clearCredentials(): Promise<void>;
  getStatus(): Promise<WidgetBridgeStatus>;
}

const WidgetBridge = registerPlugin<WidgetBridgePlugin>('WidgetBridge');

/** Store widget credentials natively and refresh the widgets. Native only. */
export async function setWidgetCredentials(creds: WidgetCredentials): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await WidgetBridge.setCredentials(creds);
    return true;
  } catch (err) {
    logger.debug('[nativeWidgets] setCredentials failed', err);
    return false;
  }
}

/** Wipe stored widget credentials (widgets show the signed-out state). */
export async function clearWidgetCredentials(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await WidgetBridge.clearCredentials();
    return true;
  } catch (err) {
    logger.debug('[nativeWidgets] clearCredentials failed', err);
    return false;
  }
}

/** Whether a widget key is stored on this device (and which key id). */
export async function getWidgetBridgeStatus(): Promise<WidgetBridgeStatus> {
  if (!Capacitor.isNativePlatform()) return { configured: false, keyId: null };
  try {
    return await WidgetBridge.getStatus();
  } catch (err) {
    logger.debug('[nativeWidgets] getStatus failed', err);
    return { configured: false, keyId: null };
  }
}

let widgetDeepLinksReady = false;

/** Route widget-tap deep links into the app. Call once at boot (native only). */
export async function initWidgetDeepLinks(): Promise<void> {
  if (widgetDeepLinksReady || !Capacitor.isNativePlatform()) return;
  widgetDeepLinksReady = true;

  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('appUrlOpen', ({ url }) => {
      handleWidgetDeepLink(url);
    });
  } catch (err) {
    logger.debug('[nativeWidgets] deep-link listener registration failed', err);
    widgetDeepLinksReady = false;
  }
}

function handleWidgetDeepLink(url: string): void {
  if (!url || !url.includes('://widget')) return;

  let dest: string | null = null;
  try {
    dest = new URL(url).searchParams.get('dest');
  } catch {
    dest = null;
  }

  // Full navigation (not router push) — the app may be cold-starting from the
  // widget tap and the shell re-boots into the authed layout either way.
  window.location.href = dest === 'inbox' ? '/inbox' : '/business';
}
